import { MIN_PASSWORD_LENGTH } from "@vidimo/shared/contracts";
import type * as React from "react";
import {
  type ChangeEvent,
  type KeyboardEvent,
  type RefObject,
  useCallback,
  useRef,
} from "react";
import { useTranslations } from "use-intl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Full-height centered layout for the pre-app auth screens.
 *
 * The inner pane reserves the strip the software keyboard covers via
 * `--keyboard-height` (mirrored from the native keyboard by useKeyboardInset).
 * When the keyboard opens, the form re-centers in the strip above it, keeping
 * the focused field and the submit button reachable, and the screen scrolls
 * when the content can't fit.
 */
export function AuthScreen({ children }: { children: React.ReactNode }) {
  return (
    <div className="h-dvh overflow-y-auto bg-background">
      <div className="flex min-h-full flex-col items-center justify-center px-6 pt-[max(2rem,env(safe-area-inset-top))] pb-[max(2rem,env(safe-area-inset-bottom),var(--keyboard-height,0px))]">
        {children}
      </div>
    </div>
  );
}

/** A form screen's centred title and the line under it. */
export function FormHeading({
  description,
  title,
}: {
  description: React.ReactNode;
  title: React.ReactNode;
}) {
  return (
    <div className="space-y-2 text-center">
      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

/**
 * A screen that only says one thing (a reset done, a link that failed) and
 * offers the way on: its actions are the children.
 */
export function AuthNotice({
  children,
  description,
  title,
}: {
  children: React.ReactNode;
  description: React.ReactNode;
  title: React.ReactNode;
}) {
  return (
    <AuthScreen>
      <div className="w-full max-w-sm space-y-4 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        <p className="text-sm text-muted-foreground">{description}</p>
        {children}
      </div>
    </AuthScreen>
  );
}

/**
 * The destructive banner every auth screen shows above its submit button.
 * Renders nothing when there is no error, so call sites read as one line.
 */
export function AuthError({ error }: { error: string | null }) {
  if (!error) {
    return null;
  }
  return (
    <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
      {error}
    </div>
  );
}

/**
 * Enter in this field moves focus to `next` until the form is complete, and
 * submits it once it is.
 *
 * The software keyboard's "next" key fires Enter, and the form's implicit
 * submit would bounce off a still-empty required field below. Fields a
 * password manager already filled must not swallow Enter. Hand each field the
 * ref of the one after it.
 */
export function useEnterFocuses(
  next: RefObject<HTMLInputElement | null>
): (e: KeyboardEvent<HTMLInputElement>) => void {
  return useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !e.currentTarget.form?.checkValidity()) {
        e.preventDefault();
        next.current?.focus();
      }
    },
    [next]
  );
}

/**
 * The new password and its confirmation, as both password-reset routes ask
 * for them. Enter in the first moves to the second.
 */
export function NewPasswordFields({
  confirmPassword,
  disabled,
  onConfirmPasswordChange,
  onPasswordChange,
  password,
  passwordRef,
}: {
  confirmPassword: string;
  disabled: boolean;
  onConfirmPasswordChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  password: string;
  passwordRef?: RefObject<HTMLInputElement | null>;
}) {
  const t = useTranslations("auth.authScreen");
  const confirmPasswordRef = useRef<HTMLInputElement>(null);
  const handlePasswordEnter = useEnterFocuses(confirmPasswordRef);
  const handlePasswordChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => onPasswordChange(e.target.value),
    [onPasswordChange]
  );
  const handleConfirmPasswordChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) =>
      onConfirmPasswordChange(e.target.value),
    [onConfirmPasswordChange]
  );
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="password">{t("newPasswordLabel")}</Label>
        <Input
          autoComplete="new-password"
          disabled={disabled}
          enterKeyHint="next"
          id="password"
          onChange={handlePasswordChange}
          onKeyDown={handlePasswordEnter}
          placeholder={t("newPasswordPlaceholder")}
          ref={passwordRef}
          required
          type="password"
          value={password}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="confirmPassword">{t("confirmPasswordLabel")}</Label>
        <Input
          autoComplete="new-password"
          disabled={disabled}
          enterKeyHint="go"
          id="confirmPassword"
          onChange={handleConfirmPasswordChange}
          placeholder={t("confirmPasswordPlaceholder")}
          ref={confirmPasswordRef}
          required
          type="password"
          value={confirmPassword}
        />
      </div>
    </>
  );
}

/** Why a new password and its confirmation cannot be sent, or null. */
export function useNewPasswordProblem(): (
  password: string,
  confirmPassword: string
) => string | null {
  const t = useTranslations("auth.authScreen");
  return useCallback(
    (password: string, confirmPassword: string) => {
      if (password.length < MIN_PASSWORD_LENGTH) {
        return t("errorPasswordTooShort");
      }
      if (password !== confirmPassword) {
        return t("errorPasswordsMismatch");
      }
      return null;
    },
    [t]
  );
}

/**
 * Terminal screen for both password-reset routes — the code flow
 * (forgot-password) and the emailed-token flow (reset-password). Only the
 * route in is different; what the host sees once the password is changed is
 * not, so the two flows share this rather than each spelling it out.
 */
export function PasswordResetDone({ onSignIn }: { onSignIn: () => void }) {
  const t = useTranslations("auth.authScreen");
  return (
    <AuthNotice
      description={t("passwordResetDescription")}
      title={t("passwordResetTitle")}
    >
      <Button className="w-full" onClick={onSignIn}>
        {t("signIn")}
      </Button>
    </AuthNotice>
  );
}
